import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { signalRService } from '@/services/signalRService';
const HUB_EVENT = 'EmailAssetGenerationEvent';
const groupName = (assetId) => `emailasset-${assetId}`;
/**
 * ORB-243: tracks in-flight bulk email-asset generations and joins one
 * SignalR group per assetId so the asset list can show a per-row
 * "Generating…" badge until each draft lands.
 *
 * Refresh policy: caller is notified per-asset on `rebuilt` and is expected
 * to do a *silent* asset-list refetch (no loading skeleton) so each row
 * updates in place. If the user navigates away or reloads mid-batch the
 * in-flight tracking is lost — re-mounting the tab just shows whatever
 * drafts have populated server-side. v2 will persist this.
 *
 * Caveat — the hub payload is `{ type, content }` only. `rebuilt` content is
 * `asset:{assetId}:v{ticks}` so completions can be attributed to a specific
 * asset; `rebuilding` and `error` carry generic strings and cannot be
 * correlated to an asset on the client. v1 therefore treats every tracked id
 * as "Generating…" until rebuilt drops it, and surfaces error events as a
 * single toast rather than a per-row error state.
 */
export function useBulkEmailGeneration({ onAssetCompleted, onGenerationError, } = {}) {
    const [inFlight, setInFlight] = useState(new Set());
    // Snapshot for use inside the SignalR event handler (which we want to
    // register once, not re-bind on every state change).
    const inFlightRef = useRef(inFlight);
    inFlightRef.current = inFlight;
    const onAssetCompletedRef = useRef(onAssetCompleted);
    onAssetCompletedRef.current = onAssetCompleted;
    const onGenerationErrorRef = useRef(onGenerationError);
    onGenerationErrorRef.current = onGenerationError;
    // Groups we've actually joined on the server. Distinct from `inFlight`
    // because joins are async — we add to this only after the server ack lands.
    const joinedRef = useRef(new Set());
    const handlerRef = useRef(null);
    useEffect(() => {
        const desired = new Set(Array.from(inFlight, groupName));
        const joined = joinedRef.current;
        const toJoin = Array.from(desired).filter(g => !joined.has(g));
        const toLeave = Array.from(joined).filter(g => !desired.has(g));
        if (toJoin.length === 0 && toLeave.length === 0)
            return;
        let cancelled = false;
        (async () => {
            // Joins and leaves are independent network calls — fan them out so
            // tracking N assets stays roughly constant-time instead of N×RTT.
            await Promise.all(toJoin.map(async (g) => {
                try {
                    await signalRService.joinGroup(g);
                    if (!cancelled)
                        joined.add(g);
                }
                catch (err) {
                    console.error('Bulk email-gen: failed to join group', g, err);
                }
            }));
            if (cancelled)
                return;
            // Hook the shared event handler the first time we have a live
            // group on the connection. signalRService.on is a no-op while no
            // connection exists, so we have to wait until a join completes.
            if (joined.size > 0 && !handlerRef.current) {
                const handler = (payload) => {
                    const raw = payload;
                    const type = (raw.Type ?? raw.type);
                    const content = (raw.Content ?? raw.content);
                    switch (type) {
                        case 'rebuilt': {
                            const assetId = (content ?? '').split(':')[1];
                            if (!assetId || !inFlightRef.current.has(assetId))
                                return;
                            setInFlight(prev => {
                                if (!prev.has(assetId))
                                    return prev;
                                const next = new Set(prev);
                                next.delete(assetId);
                                return next;
                            });
                            onAssetCompletedRef.current?.(assetId);
                            break;
                        }
                        case 'error':
                            onGenerationErrorRef.current?.(content ?? 'Email generation failed');
                            break;
                    }
                };
                handlerRef.current = handler;
                signalRService.on(HUB_EVENT, handler);
            }
            await Promise.all(toLeave.map(async (g) => {
                try {
                    await signalRService.leaveGroup(g);
                    joined.delete(g);
                }
                catch (err) {
                    console.error('Bulk email-gen: failed to leave group', g, err);
                }
            }));
            if (joined.size === 0 && handlerRef.current) {
                signalRService.off(HUB_EVENT, handlerRef.current);
                handlerRef.current = null;
            }
        })();
        return () => { cancelled = true; };
    }, [inFlight]);
    // Final unmount cleanup: drop any still-joined groups and detach handler.
    useEffect(() => {
        return () => {
            const groups = Array.from(joinedRef.current);
            joinedRef.current.clear();
            const handler = handlerRef.current;
            handlerRef.current = null;
            if (handler)
                signalRService.off(HUB_EVENT, handler);
            for (const g of groups) {
                signalRService.leaveGroup(g).catch(() => { });
            }
        };
    }, []);
    const startTracking = useCallback((assets) => {
        if (assets.length === 0)
            return;
        setInFlight(prev => {
            const next = new Set(prev);
            for (const a of assets)
                next.add(a.AssetId);
            return next;
        });
    }, []);
    const isGenerating = useCallback((assetId) => {
        return inFlight.has(assetId);
    }, [inFlight]);
    return {
        startTracking,
        isGenerating,
        inFlightCount: inFlight.size,
    };
}
